/**
 * Dizayn-tokenlar — NF-601 (brend), NF-602 (o'zbek interfeysi), NF-603 (dizayn-sistema).
 *
 * Bu fayl rang va uslublarning YAGONA MANBAI. Komponentlarda HEX qiymat
 * yozilmaydi — shu yerdan yoki Tailwind sinfi (`bg-brand`) orqali olinadi.
 * `tailwind.config.js` shu fayldan import qiladi, ya'ni ikkisi ajralib
 * qololmaydi.
 *
 * Manba: **PDP Design System** (design.pdp.uz) — butun PDP ekotizimi uchun
 * yagona vizual til. Eski `PDP Pulse brandbook v1.0` qiymatlari shu DS ga
 * almashtirildi; eksport NOMLARI saqlangan, sahifalar ularga tayanadi.
 */

/**
 * PDP DS — to'liq token lug'ati. Sahifalar va diagrammalar (ECharts hex
 * talab qiladi) uchun. Tailwind sinfi yetmagan joyda SHU obyekt ishlatiladi,
 * hech qachon xom hex emas.
 */
export const DS = {
  /** Brend ko'k shkalasi (brand ramp). */
  brand: {
    50: "#E7EFFE",
    100: "#CFDEFC",
    200: "#A3C1FA",
    300: "#6E9DF7",
    400: "#3E7CF4",
    600: "#2168F2", // CTA, asosiy
    700: "#1550C1", // hover
    800: "#11409C", // pressed
  },
  /** Navy — matn va yon panel foni. */
  navy: "#0B1240",
  /** Deep — chuqur ko'k (dekor). */
  deep: "#000C7A",
  /** Aksent: coinlar, muhim raqam. */
  yellow: "#FFC700",
  /** Aksent: deadline, o'qilmagan. */
  pink: "#FF0064",
  /** Matn ranglari. */
  text: {
    primary: "#0B1240",
    body: "rgba(11,18,64,0.72)",
    tertiary: "#5A6488",
    muted: "#8A93B4",
  },
  /** Semantik uchliklar: fill (fon) / fg (matn) / solid (nuqta, chiziq). */
  semantic: {
    success: { fill: "#E9F7F1", fg: "#0E7A57", solid: "#17B26A" },
    warning: { fill: "#FFF3E0", fg: "#9A5B08", solid: "#F0A422" },
    error: { fill: "#FDECEB", fg: "#B93229", solid: "#D0342C" },
    info: { fill: "#E7EFFE", fg: "#11409C", solid: "#2168F2" },
    /** «Bugun» — hot holat. */
    hot: { fill: "#FFF0F5", fg: "#C7004E", solid: "#FF0064" },
  },
  /** Soyalar — FAQAT ko'k, qora soya taqiqlanadi. */
  shadow: {
    card: "0 16px 40px rgba(0,12,122,0.05)",
    raised: "0 22px 46px rgba(0,12,122,0.09)",
    navy: "0 24px 56px rgba(0,12,122,0.22)",
    modal: "0 32px 64px rgba(0,12,122,0.18)",
    cta: "0 10px 24px rgba(33,104,242,0.28)",
  },
  /**
   * Gradientlar — faqat ikkita, uchinchisi taqiqlanadi.
   * `navy` — design.pdp.uz manba spetsifikatsiyasidagi aniq qiymat: 3 nuqtali,
   * 180deg (tepadan pastga), yon panel foni uchun.
   */
  gradient: {
    hero: "linear-gradient(120deg,#1B56DB 0%,#1445C4 58%,#2748B6 100%)",
    navy: "linear-gradient(180deg,#1B56DB 0%,#1445C4 55%,#2748B6 100%)",
  },
  /** Yuzalar. */
  surface: {
    canvas: "#FAFBFE",
    card: "#FFFFFF",
    subtle: "#EFF3FD",
    border: "rgba(11,18,64,0.14)",
    hairline: "rgba(11,18,64,0.08)",
    /** Diagramma to'ri (grid) chizig'i — DS grafik me'yori. */
    chartGrid: "#DFE4F0",
  },
} as const;

/**
 * Brend palitrasi — eski nomlar saqlangan (sahifalar tayanadi), qiymatlar
 * PDP DS niki: `blue` endi brand600, `beat` — sariq aksent, `deep` — deep ko'k.
 */
export const BRAND_COLORS = {
  /** Asosiy ko'k — CTA, birlamchi harakat, faol holat (brand600). */
  blue: DS.brand[600],
  /** Aksent — muhim raqam, jonli holat, indikator (DS yellow). */
  beat: DS.yellow,
  /** Chuqur ko'k — dekor va gradient asosi. */
  deep: DS.deep,
  white: "#FFFFFF",
  black: DS.navy,
} as const;

/** Interfeys yuzalari va matn ranglari — PDP DS qiymatlari bilan. */
export const UI_COLORS = {
  /** Sahifa foni. */
  canvas: DS.surface.canvas,
  /** Karta va panel foni. */
  surface: DS.surface.card,
  /** Ikkilamchi yuza — teg, kod bloki, ajratilgan qator. */
  subtle: DS.surface.subtle,
  /** Asosiy chegara. */
  border: DS.surface.border,
  /** Ingichka ichki chegara (jadval qatorlari). */
  hairline: DS.surface.hairline,
  /** Sarlavha matni (navy). */
  ink: DS.text.primary,
  /** Asosiy matn. */
  body: DS.text.body,
  /** So'ngan matn — izoh, birlik, texnik yozuv. */
  muted: DS.text.muted,
  /** Uchinchi darajali matn. */
  tertiary: DS.text.tertiary,
  /** To'q (navy) fondagi matn. */
  onDeep: "rgba(255,255,255,0.78)",
  /** To'q fondagi so'ngan yozuv. */
  onDeepMuted: "rgba(255,255,255,0.55)",
} as const;

/**
 * Holat ranglari — PDP DS semantik uchliklaridan. Eski kalitlar saqlangan:
 * `good`/`warn`/`bad` — MATN uchun (fg), `*Solid` — nuqta/chiziq uchun,
 * `*Soft` — fon (fill). Rang yolg'iz status tashimaydi — doim matn/dot bilan.
 */
export const STATUS_COLORS = {
  good: DS.semantic.success.fg,
  goodSoft: DS.semantic.success.fill,
  goodSolid: DS.semantic.success.solid,
  warn: DS.semantic.warning.fg,
  warnSoft: DS.semantic.warning.fill,
  warnSolid: DS.semantic.warning.solid,
  warnBorder: "#F5D9A8",
  warnInk: DS.semantic.warning.fg,
  bad: DS.semantic.error.fg,
  badSoft: DS.semantic.error.fill,
  badSolid: DS.semantic.error.solid,
  info: DS.semantic.info.solid,
  infoSoft: DS.semantic.info.fill,
  infoInk: DS.semantic.info.fg,
  hot: DS.semantic.hot.fg,
  hotSoft: DS.semantic.hot.fill,
  hotSolid: DS.semantic.hot.solid,
} as const;

export type BrandColorName = keyof typeof BRAND_COLORS;

/**
 * Quvur holatlari (PP-415) — rang VA o'zbekcha nom bir joyda.
 * Ikkisini ajratish «rangi yangilandi, matni eski qoldi» xatosiga olib keladi.
 */
export const CALL_STATUS_STYLE = {
  queued: { label: "Navbatda", className: "bg-subtle text-body ring-border" },
  processing: { label: "Jarayonda", className: "bg-info-soft text-info-ink ring-brand/20" },
  ready: { label: "Tayyor", className: "bg-good-soft text-good ring-good/20" },
  failed: { label: "Xato", className: "bg-bad-soft text-bad ring-bad/20" },
  paused: { label: "To'xtatilgan", className: "bg-warn-soft text-warn-ink ring-warn/30" },
} as const satisfies Record<string, { label: string; className: string }>;

/** Bog'lanish holati (PP-302) — TZ uchta holatni belgilaydi. */
export const CONNECTION_STATUS_LABEL: Record<string, string> = {
  connected: "Bog'langan",
  missed: "Javobsiz",
  not_connected: "Ulanmagan aloqa",
};

/** Yo'nalish. */
export const DIRECTION_LABEL: Record<string, string> = {
  inbound: "Kiruvchi",
  outbound: "Chiquvchi",
  unknown: "Noma'lum",
};

/** Rollar (TZ 2.2 RBAC jadvali). */
export const ROLE_LABEL: Record<string, string> = {
  owner: "Rahbar",
  rop: "ROP",
  manager: "Menejer",
  viewer: "Kuzatuvchi",
};

/** «Biznes pulsi» holati (PP-601) — badge: fill/fg + solid dot. */
export const PULSE_STATUS_STYLE: Record<string, { label: string; className: string; dot: string }> =
  {
    stable: {
      label: "Barqaror",
      className: "bg-good-soft text-good ring-good/25",
      dot: "bg-good-solid",
    },
    attention: {
      label: "E'tibor talab qiladi",
      className: "bg-warn-soft text-warn-ink ring-warn/30",
      dot: "bg-warn-solid",
    },
    risk: {
      label: "Xavfli",
      className: "bg-bad-soft text-bad ring-bad/25",
      dot: "bg-bad-solid",
    },
  };

/** Gaming sabab kodlari (PP-327) — foydalanuvchiga tushunarli matn. */
export const SUSPICION_REASON_LABEL: Record<string, string> = {
  no_client_speech: "Mijoz umuman gapirmagan",
  repeated_operator_text: "Operator bir xil matnni takrorlagan",
  operator_monologue: "Suhbat monologga aylangan",
  criteria_keyword_stuffing: "Mezon iboralari ketma-ket o'qilgan",
};

/**
 * Ball chegaralari — 0–100 shkalasi.
 *
 * TZ da bu chegaralar belgilanmagan; ular UI konvensiyasi va pilotda
 * kalibrlanadi (QARORLAR.md). Shuning uchun ular bitta joyda turadi va
 * o'zgartirish bitta qatordan iborat.
 */
export const SCORE_THRESHOLD = { good: 80, warn: 60 } as const;

/** Ball matnining rangi. */
export function scoreColor(score: number | null): string {
  if (score === null) {
    return "text-muted";
  }
  if (score >= SCORE_THRESHOLD.good) {
    return "text-good";
  }
  if (score >= SCORE_THRESHOLD.warn) {
    return "text-warn";
  }
  return "text-bad";
}

/** Foiz uchun progress rangi — chiziq SOLID rangda chiziladi. */
export function percentBarColor(percent: number): string {
  if (percent >= SCORE_THRESHOLD.good) {
    return "bg-good-solid";
  }
  if (percent >= SCORE_THRESHOLD.warn) {
    return "bg-warn-solid";
  }
  return "bg-bad-solid";
}

/** Diagrammalarda ishlatiladigan HEX (ECharts Tailwind sinfini tushunmaydi). */
export function scoreHex(score: number | null): string {
  if (score === null) {
    return UI_COLORS.muted;
  }
  if (score >= SCORE_THRESHOLD.good) {
    return STATUS_COLORS.goodSolid;
  }
  if (score >= SCORE_THRESHOLD.warn) {
    return STATUS_COLORS.warnSolid;
  }
  return STATUS_COLORS.badSolid;
}

/** `mm:ss` (Ilova C «O'rtacha davomiylik» formati — UI qatlamining ishi). */
export function formatDuration(seconds: number | null): string {
  if (seconds === null) {
    return "—";
  }
  const minutes = Math.floor(seconds / 60);
  return `${String(minutes)}:${String(seconds % 60).padStart(2, "0")}`;
}

/** Sana va vaqt — o'zbek lokalida. */
export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString("uz-UZ", { dateStyle: "short", timeStyle: "short" });
}

/** Faqat sana. */
export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("uz-UZ", { day: "2-digit", month: "short" });
}

/**
 * Sonni o'zbek lokalida ajratuvchi bilan.
 * `Intl` bo'sh joyni ajratuvchi qilib beradi — bu o'zbek yozuv me'yori.
 */
export function formatNumber(value: number, fractionDigits = 0): string {
  return new Intl.NumberFormat("uz-UZ", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

/**
 * Metrika ta'riflari — TZ Ilova C «yagona formulalar lug'ati».
 *
 * NIMA UCHUN INTERFEYSDA KO'RSATILADI: bir xil nomdagi metrikaning ikki xil
 * ta'rifi — rahbarning mahsulotga ishonchini yo'q qiladigan eng katta xato.
 * Foydalanuvchi «Tahlil qilingan» nimani anglatishini so'ramasdan bilishi
 * kerak, aks holda u raqamni o'z boshicha talqin qiladi.
 *
 * Matnlar FORMULANI takrorlamaydi (u `packages/core` da, 5-qoida) — ular
 * formulani SO'Z BILAN aytadi. Formula o'zgarsa bu matn ham o'zgaradi.
 *
 * TZ ID xaritasi (rule 9): suspicious=PP-327, lowConfidence=PP-329,
 * failed=PP-415, missedInbound=PP-1205.
 */
export const METRIC_DEFINITION: Record<string, string> = {
  conversationQuality:
    "Baholangan savdo suhbatlarining o'rtacha balli (0–100). Baholanmaydigan, shubhali va past ishonchli suhbatlar hisobga kirmaydi.",
  outboundConnectRate:
    "Bog'langan chiquvchi qo'ng'iroqlarning barcha chiquvchi urinishlarga nisbati. Texnik nosozlik bilan tugaganlari hisobga kirmaydi.",
  averageCallDuration:
    "Faqat BOG'LANGAN suhbatlarning o'rtacha davomiyligi. Javobsiz qo'ng'iroqlar o'rtachani sun'iy pasaytirmasligi uchun chiqarilgan.",
  conversionRate:
    "Davr ichida bitimga aylangan lidlar ulushi. Lid qaysi kuni yaratilganiga qarab hisoblanadi, bitim qaysi kuni yopilganiga emas.",
  calls: "Davr ichida quvurga tushgan barcha qo'ng'iroqlar soni (dublikatlar chiqarilgan).",
  analyzed: "Tahlili muvaffaqiyatli yakunlangan qo'ng'iroqlar. Xato bilan tugaganlar kirmaydi.",
  salesCalls: "Savdo suhbati deb tasniflangan va baholanadigan qo'ng'iroqlar (Ilova B oilalari).",
  operationalCalls:
    "Operatsion qo'ng'iroqlar (yetkazib berish, texnik yordam va h.k.) — ular ballanmaydi va o'rtacha ballni buzmaydi.",
  // suspicious=PP-327, lowConfidence=PP-329, failed=PP-415, missedInbound=PP-1205.
  suspicious:
    "Baholash tizimini aldashga urinish belgilari topilgan suhbatlar. Ular baholanmaydi va reytingga kirmaydi.",
  lowConfidence:
    "Transkript ishonchi biznes chegarasidan past bo'lgan suhbatlar. Ball noto'g'ri bo'lishi mumkin.",
  pending: "Navbatda yoki qayta ishlanmoqda.",
  failed: "Quvur bosqichida xato bo'lgan va tahlil qilinmagan qo'ng'iroqlar.",
  missedInbound: "Ish vaqti oynasida javobsiz qolgan kiruvchi qo'ng'iroqlar.",

  // ── PP-1221: qo'shimcha metrikalar (Call/Team/Reports/My/Usage sahifalari) ──
  finalScore:
    "Mezon ballari (0–3) kategoriya vazni orqali 0–100 shkalaga normallashtiriladi, so'ng qizil bayroq jarimalari ayiriladi.",
  talkRatio:
    "Menejer gapirgan vaqtning suhbat davomiyligiga nisbati. Tavsiya etilgan oraliq — 40–60%.",
  criterionScore:
    "Har mezon 0–3 ball bilan baholanadi: rubrikadagi ta'rifga qanchalik mos kelgani bo'yicha.",
  teamQuality:
    "Jamoaning shu davrdagi o'rtacha balli — faqat baholanadigan savdo suhbatlari bo'yicha.",
  teamMissedInbound: "Ish vaqtida javobsiz qolgan kiruvchi qo'ng'iroqlar — jamoa kesimida.",
  dailyReportCalls: "Kun davomida quvurdan o'tgan barcha qo'ng'iroqlar soni.",
  dailyReportQuality: "Kun ichida baholangan suhbatlarning o'rtacha balli.",
  weeklyScore: "Oxirgi 7 kundagi baholangan suhbatlar o'rtachasi.",
  todayScore: "Bugungi baholangan suhbatlar o'rtacha balli.",
  usageAnalyzed: "Oxirgi tanlangan davrda yakunlangan tahlillar soni.",
  usageLlmTokens: "AI modeliga yuborilgan/undan qaytgan so'z birliklari — sarfni belgilaydi.",
  usageSttSeconds:
    "Transkripsiya qilingan audio uzunligi (soniya) — nutq tanish sarfi shu bo'yicha hisoblanadi.",
  redFlags: "Qat'iy qoida buzilgan suhbatlar — har biri yakuniy balldan jarima sifatida ayiriladi.",
};

/**
 * Bildirishnoma turlari — nom va rang (PP-905).
 *
 * `alert_rules.type` bilan bir xil kalitlar. Yangi tur qo'shilsa bu yerga ham
 * qo'shiladi; yozuvi yo'q tur kod bilan ko'rsatiladi va u darhol sezilib
 * turadi (jimgina «boshqa» guruhiga tushib ketmaydi).
 */
// TZ ID xaritasi (rule 9): missed_call=PP-901, red_flag=PP-902,
// sla_first_call/sla_callback=PP-504, integration_down=PP-107,
// limit_near=PP-1216, system=PP-905.
export const NOTIFICATION_TYPE_STYLE: Record<string, { label: string; className: string }> = {
  missed_call: {
    label: "Javobsiz qo'ng'iroq",
    className: "bg-warn-soft text-warn-ink ring-warn/30",
  },
  red_flag: {
    label: "Qizil bayroq",
    className: "bg-hot-soft text-hot ring-hot-solid/25",
  },
  sla_first_call: {
    label: "Birinchi qo'ng'iroq kechikdi",
    className: "bg-warn-soft text-warn-ink ring-warn/30",
  },
  sla_callback: {
    label: "Qayta qo'ng'iroq kechikdi",
    className: "bg-warn-soft text-warn-ink ring-warn/30",
  },
  integration_down: {
    label: "Integratsiya uzildi",
    className: "bg-bad-soft text-bad ring-bad/25",
  },
  limit_near: {
    label: "Chegaraga yaqinlashildi",
    className: "bg-info-soft text-info-ink ring-brand/20",
  },
  system: {
    label: "Tizim xabari",
    className: "bg-subtle text-body ring-border",
  },
};

/** «3 daqiqa oldin» ko'rinishidagi nisbiy vaqt. */
export function formatRelative(iso: string): string {
  const seconds = Math.round((Date.now() - new Date(iso).getTime()) / 1000);

  if (seconds < 60) {
    return "hozirgina";
  }
  if (seconds < 3600) {
    return `${String(Math.floor(seconds / 60))} daqiqa oldin`;
  }
  if (seconds < 86_400) {
    return `${String(Math.floor(seconds / 3600))} soat oldin`;
  }
  if (seconds < 7 * 86_400) {
    return `${String(Math.floor(seconds / 86_400))} kun oldin`;
  }

  return formatDateTime(iso);
}
